import type { ConstraintInputType } from '../../../config/optimizePlanConfig';
import type { MetricFormat } from '../../../config/metrics';
import type { ConstraintGroup, ConstraintInputValue, Level } from '../components/OptimizePlan/types';

export type ConstraintRowKind = 'kpi' | 'level' | 'attribute' | 'status' | 'add_level';

/**
 * A node in the three-tier constraints tree, flattened into the shape AntD's
 * `Table` expects. `kind` decides how every cell renders, so the column
 * definitions stay generic and the branching lives in one place.
 */
export interface ConstraintTableRow {
    rowKey: string;
    kind: ConstraintRowKind;
    label: string;
    /** Left padding step: 0 for a KPI, 1 for a level block, 2 for an attribute row. */
    depth: number;
    constraintName: string;
    level?: Level;
    attributeName?: string;
    fields?: Record<string, unknown>;
    input?: ConstraintInputValue;
    inputInvalid?: boolean;
    inputType?: ConstraintInputType;
    format?: MetricFormat;
    addableLevels?: { key: Level; label: string; disabled: boolean }[];
    children?: ConstraintTableRow[];
}

/**
 * Row keys compose config keys and, at the leaf, the backend attribute name — the
 * same pass-the-key-through convention every other TPOGen table row uses, so a
 * locator built from the tenant config and an API response resolves without a
 * separate lookup.
 */
export function buildConstraintTableRows(
    groups: ConstraintGroup[],
    statusLabels: { loading: string; error: string; empty: string },
): ConstraintTableRow[] {
    return groups.map((group) => {
        const levelRows: ConstraintTableRow[] = group.levels.map((block) => {
            const levelKey = `${group.name}_${block.level}`;

            const children: ConstraintTableRow[] =
                block.status === 'ready'
                    ? block.rows.map((row) => ({
                          rowKey: `${levelKey}_${row.attributeName}`,
                          kind: 'attribute' as const,
                          label: row.attributeName,
                          depth: 2,
                          constraintName: group.name,
                          level: block.level,
                          attributeName: row.attributeName,
                          fields: row.fields,
                          input: row.input,
                          inputInvalid: row.hasInvalidInput,
                          inputType: group.inputType,
                          format: group.format,
                      }))
                    : [
                          {
                              rowKey: `${levelKey}_status`,
                              kind: 'status' as const,
                              label: statusLabels[block.status],
                              depth: 2,
                              constraintName: group.name,
                              level: block.level,
                          },
                      ];

            return {
                rowKey: levelKey,
                kind: 'level' as const,
                label: block.levelLabel,
                depth: 1,
                constraintName: group.name,
                level: block.level,
                children,
            };
        });

        return {
            rowKey: group.name,
            kind: 'kpi' as const,
            label: group.label,
            depth: 0,
            constraintName: group.name,
            // Always present, so a KPI with no level yet still opens onto its Add Level control.
            children: [
                ...levelRows,
                {
                    rowKey: `${group.name}_add_level`,
                    kind: 'add_level' as const,
                    label: '',
                    depth: 1,
                    constraintName: group.name,
                    addableLevels: group.addableLevels,
                },
            ],
        };
    });
}

/** Every key that can be expanded, so the table can default to fully open. */
export function collectExpandableKeys(rows: ConstraintTableRow[]): string[] {
    const keys: string[] = [];
    const walk = (nodes: ConstraintTableRow[]) => {
        for (const node of nodes) {
            if (node.children && node.children.length > 0) {
                keys.push(node.rowKey);
                walk(node.children);
            }
        }
    };
    walk(rows);
    return keys;
}
